import { CLASS_NAMES } from "@/core/constants/class-names";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";

export const SPIFF_SECTIONS = {
  ...SPIFF_RULE_EVENTS,
  dismissed: "dismissed",
} as const;

export const SPIFF_TRIGGER_CLASS_NAMES = {
  [CLASS_NAMES.Customer]: CLASS_NAMES.Customer,
  [CLASS_NAMES.Referral]: CLASS_NAMES.Referral,
  [CLASS_NAMES.Review]: CLASS_NAMES.Review,
  [CLASS_NAMES.SpiffPhoto]: CLASS_NAMES.SpiffPhoto,
  [CLASS_NAMES.ServiceTitanInvoiceItem]: CLASS_NAMES.ServiceTitanInvoiceItem,
  [CLASS_NAMES.SpiffTriggerPlaceholder]: CLASS_NAMES.SpiffTriggerPlaceholder,
} as const;

export const SPIFF_RULE_EVENT_TO_TRIGGER_CLASS_NAME = {
  [SPIFF_RULE_EVENTS.signup]: SPIFF_TRIGGER_CLASS_NAMES.Customer,
  [SPIFF_RULE_EVENTS.referral_creation]: SPIFF_TRIGGER_CLASS_NAMES.Referral,
  [SPIFF_RULE_EVENTS.referral_completion]: SPIFF_TRIGGER_CLASS_NAMES.Referral,
  [SPIFF_RULE_EVENTS.five_star_review]: SPIFF_TRIGGER_CLASS_NAMES.Review,
  [SPIFF_RULE_EVENTS.yard_sign_photo]: SPIFF_TRIGGER_CLASS_NAMES.SpiffPhoto,
  [SPIFF_RULE_EVENTS.pricebook_item_sale]:
    SPIFF_TRIGGER_CLASS_NAMES.ServiceTitanInvoiceItem,
  [SPIFF_RULE_EVENTS.tech_generated_lead]:
    SPIFF_TRIGGER_CLASS_NAMES.SpiffTriggerPlaceholder,
  [SPIFF_RULE_EVENTS.driving_score]:
    SPIFF_TRIGGER_CLASS_NAMES.SpiffTriggerPlaceholder,
  [SPIFF_RULE_EVENTS.generic]:
    SPIFF_TRIGGER_CLASS_NAMES.SpiffTriggerPlaceholder,
} as const;

export const TRIGGER_CLASS_NAME_TO_LABEL = {
  [SPIFF_TRIGGER_CLASS_NAMES.Customer]: "Customer",
  [SPIFF_TRIGGER_CLASS_NAMES.Referral]: "Referral",
  [SPIFF_TRIGGER_CLASS_NAMES.Review]: "Review",
  [SPIFF_TRIGGER_CLASS_NAMES.SpiffPhoto]: "Photo",
  [SPIFF_TRIGGER_CLASS_NAMES.ServiceTitanInvoiceItem]: "Invoice Item",
  [SPIFF_TRIGGER_CLASS_NAMES.SpiffTriggerPlaceholder]: "Placeholder",
} as const;

export const SPIFF_EVENT_BADGE_VARIANTS = {
  [SPIFF_RULE_EVENTS.signup]: "purple",
  [SPIFF_RULE_EVENTS.referral_creation]: "orange",
  [SPIFF_RULE_EVENTS.referral_completion]: "red",
  [SPIFF_RULE_EVENTS.five_star_review]: "gold",
  [SPIFF_RULE_EVENTS.yard_sign_photo]: "aqua",
  [SPIFF_RULE_EVENTS.pricebook_item_sale]: "green",
  [SPIFF_RULE_EVENTS.tech_generated_lead]: "blue",
  [SPIFF_RULE_EVENTS.driving_score]: "magenta",
  [SPIFF_RULE_EVENTS.generic]: "gray",
} as const;

export const SPIFF_EVENT_BORDER_COLORS = {
  [SPIFF_RULE_EVENTS.signup]: "border-badge-purple",
  [SPIFF_RULE_EVENTS.referral_creation]: "border-badge-orange",
  [SPIFF_RULE_EVENTS.referral_completion]: "border-badge-red",
  [SPIFF_RULE_EVENTS.five_star_review]: "border-badge-gold",
  [SPIFF_RULE_EVENTS.yard_sign_photo]: "border-badge-aqua",
  [SPIFF_RULE_EVENTS.pricebook_item_sale]: "border-badge-green",
  [SPIFF_RULE_EVENTS.tech_generated_lead]: "border-badge-blue",
  [SPIFF_RULE_EVENTS.driving_score]: "border-badge-magenta",
  [SPIFF_RULE_EVENTS.generic]: "border-badge-gray",
} as const;

export const SPIFF_EVENT_BG_COLORS = {
  [SPIFF_RULE_EVENTS.signup]: "bg-purple-20",
  [SPIFF_RULE_EVENTS.referral_creation]: "bg-orange-20",
  [SPIFF_RULE_EVENTS.referral_completion]: "bg-red-20",
  [SPIFF_RULE_EVENTS.five_star_review]: "bg-gold-20",
  [SPIFF_RULE_EVENTS.yard_sign_photo]: "bg-aqua-20",
  [SPIFF_RULE_EVENTS.pricebook_item_sale]: "bg-green-20",
  [SPIFF_RULE_EVENTS.tech_generated_lead]: "bg-blue-20",
  [SPIFF_RULE_EVENTS.driving_score]: "bg-magenta-20",
  [SPIFF_RULE_EVENTS.generic]: "bg-gray-20",
} as const;

export const SPIFF_EVENT_HEADER_COLORS = {
  [SPIFF_RULE_EVENTS.signup]: "bg-badge-purple",
  [SPIFF_RULE_EVENTS.referral_creation]: "bg-badge-orange",
  [SPIFF_RULE_EVENTS.referral_completion]: "bg-badge-red",
  [SPIFF_RULE_EVENTS.five_star_review]: "bg-badge-gold",
  [SPIFF_RULE_EVENTS.yard_sign_photo]: "bg-badge-aqua",
  [SPIFF_RULE_EVENTS.pricebook_item_sale]: "bg-badge-green",
  [SPIFF_RULE_EVENTS.tech_generated_lead]: "bg-badge-blue",
  [SPIFF_RULE_EVENTS.driving_score]: "bg-badge-magenta",
  [SPIFF_RULE_EVENTS.generic]: "bg-badge-gray",
} as const;

// ============= Spiff Sidebar Sections =============

export const SPIFF_SIDEBAR_SECTIONS = {
  pendingSpiffs: "pendingSpiffs",
  dismissedSpiffs: "dismissedSpiffs",
  approvals: "approvals",
  spiffPhoto: "spiffPhoto",
  spiffDetail: "spiffDetail",
  payrollPeriodUser: "payrollPeriodUser",
} as const;

export type SpiffSidebarSection =
  (typeof SPIFF_SIDEBAR_SECTIONS)[keyof typeof SPIFF_SIDEBAR_SECTIONS];

// ============= Payroll Period Constants =============

export const PAYROLL_PERIOD_CONSTANTS = {
  MAX_PERIOD_HEIGHT: 600,
  TIMELINE_DOT_OFFSET: 8,
  PAST_PERIODS_COUNT: 12,
  FUTURE_PERIODS_COUNT: 4,
} as const;

// ============= CSV Export Constants =============

export const DATA_SHEET_HEADERS = {
  PAYROLL_PERIOD: [
    "Technician",
    "Signups",
    "New Referrals",
    "Closed Referrals",
    "Five-star reviews",
    "Yard Signs",
    "Pricebook Sales",
    "TGLs",
    "Driving Scores",
    "Generic",
    "Total",
  ],
} as const;

// ============= Payroll Period Status Constants =============

export const PAYROLL_PERIOD_STATUS = {
  past: "past",
  current: "current",
  future: "future",
} as const;
